import { useCallback, useEffect, useState, type JSX } from 'react';
import { QuestionForm } from '../../components/QuestionForm.tsx';
import { SaveRow } from '../../components/SaveRow.tsx';
import { Link, useParams } from 'react-router-dom';
import { numberOr, optionalNumber } from '../../lib/numbers.ts';
import { api, type BankQuestion, type QuizDetail } from '../../api.ts';
import { Markup } from '../../components/Markup.tsx';

/**
 * Assembling a quiz out of bank questions.
 *
 * The screen leans on one fact: a quiz people have already sat cannot be reshaped freely.
 * Removing a question somebody answered would change marks already given, so the server refuses
 * it — and this page says how many attempts exist before the teacher starts rearranging.
 */
/**
 * One question of a test, opened for editing inside the test itself.
 *
 * Loaded on demand: a test of thirty questions should not fetch thirty full questions with their
 * options to show a list of titles.
 *
 * The refusal it has to explain is a real one. Once a pupil has answered, the options are frozen,
 * because a stored answer names an option by id — rewriting it would change what a child is on
 * record as having chosen. The wording says what to do instead rather than leaving a teacher
 * staring at "conflict".
 */
function QuestionEditor({
  questionId,
  onSaved,
}: {
  questionId: string;
  onSaved: () => void;
}): JSX.Element {
  const [question, setQuestion] = useState<BankQuestion | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    api.authoring
      .question(questionId)
      .then((loaded) => alive && setQuestion(loaded))
      .catch((cause: unknown) =>
        alive
          ? setError(cause instanceof Error ? cause.message : 'Не удалось открыть вопрос')
          : undefined,
      );

    return () => {
      alive = false;
    };
  }, [questionId]);

  if (error) return <p className="error">{error}</p>;
  if (!question) return <p className="muted">Загружаем вопрос…</p>;

  return (
    <QuestionForm
      question={question}
      submitLabel="Сохранить вопрос"
      onSubmit={async (draft) => {
        try {
          await api.authoring.updateQuestion(questionId, {
            text_md: draft.text_md,
            explanation_md: draft.explanation_md,
            ...(draft.config ? { config: draft.config } : {}),
            ...(draft.options ? { options: draft.options } : {}),
          });
        } catch (cause) {
          const message = cause instanceof Error ? cause.message : '';

          // The server refuses to rewrite answered options. Say why, and what to do about it.
          if (message.toLowerCase().includes('answer') || message.includes('уже отвеч')) {
            throw new Error(
              'Ученики уже отвечали на этот вопрос, поэтому варианты ответа менять нельзя. ' +
                'Текст и пояснение сохранить можно — уберите правки в вариантах, ' +
                'либо добавьте в тест новый вопрос вместо этого.',
              { cause },
            );
          }

          throw cause;
        }

        onSaved();
      }}
    />
  );
}

export function QuizEditorPage(): JSX.Element {
  const { quizId } = useParams();
  const [quiz, setQuiz] = useState<QuizDetail | null>(null);
  const [bank, setBank] = useState<BankQuestion[]>([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  /** Which question is open for editing. One at a time — the form is tall. */
  const [editing, setEditing] = useState<string | null>(null);

  /* Settings are edited locally and saved in one go, so a half-typed pass mark never reaches the DB. */
  const [title, setTitle] = useState('');
  const [pass, setPass] = useState('60');
  const [attempts, setAttempts] = useState('0');
  const [limit, setLimit] = useState('');
  const [shuffleQ, setShuffleQ] = useState(false);
  const [shuffleO, setShuffleO] = useState(false);
  const [review, setReview] = useState<QuizDetail['show_review']>('after_pass');

  const load = useCallback((): void => {
    if (!quizId) return;
    api.authoring
      .quiz(quizId)
      .then((loaded) => {
        setQuiz(loaded);
        setTitle(loaded.title);
        setPass(String(Math.round(loaded.pass_fraction * 100)));
        setAttempts(String(loaded.max_attempts));
        setLimit(loaded.time_limit_min === null ? '' : String(loaded.time_limit_min));
        setShuffleQ(loaded.shuffle_questions);
        setShuffleO(loaded.shuffle_options);
        setReview(loaded.show_review);
      })
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить тест'),
      );
  }, [quizId]);

  useEffect(load, [load]);

  const loadBank = useCallback((term: string): void => {
    api.authoring
      .questions(term || undefined)
      .then(setBank)
      .catch(() => setBank([]));
  }, []);

  useEffect(() => loadBank(''), [loadBank]);

  async function run(action: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError('');
    try {
      await action();
      load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  }

  if (error && !quiz) {
    return (
      <div className="stack" style={{ paddingTop: 20 }}>
        <div className="notice notice--accent">{error}</div>
        <Link to="/admin" className="back">
          ← Курсы
        </Link>
      </div>
    );
  }

  if (!quiz || !quizId) return <p className="muted">Загружаем…</p>;

  const id = quizId;
  const used = new Set(quiz.slots.map((slot) => slot.question_id));
  const totalMarks = quiz.slots.reduce((sum, slot) => sum + slot.max_mark, 0);

  return (
    <>
      <div className="page-head">
        {quiz.lesson_id ? (
          <Link to={`/admin/lessons/${quiz.lesson_id}`} className="back">
            ← К уроку
          </Link>
        ) : null}
        <h1>{quiz.title}</h1>
        <p className="muted">
          Вопросов: {quiz.slots.length} · всего баллов: {totalMarks} · попыток учеников:{' '}
          {quiz.attempt_count}
        </p>
        {quiz.attempt_count > 0 ? (
          <div className="notice notice--accent">
            Тест уже проходили. Вопрос, на который успели ответить, убрать нельзя — иначе изменились
            бы уже выставленные оценки.
          </div>
        ) : null}
      </div>

      {error ? <p className="error">{error}</p> : null}

      <section className="editcard stack">
        <strong>Настройки</strong>

        <div className="field">
          <label htmlFor="quiz-title">Название</label>
          <input
            id="quiz-title"
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        <div className="formrow">
          <div className="field">
            <label htmlFor="quiz-pass">Проходной балл, %</label>
            <input
              id="quiz-pass"
              className="input"
              type="number"
              min={0}
              max={100}
              value={pass}
              onChange={(e) => setPass(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="quiz-att">Попыток (0 — без ограничения)</label>
            <input
              id="quiz-att"
              className="input"
              type="number"
              min={0}
              max={50}
              value={attempts}
              onChange={(e) => setAttempts(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="quiz-lim">Время, мин</label>
            <input
              id="quiz-lim"
              className="input"
              type="number"
              min={1}
              max={600}
              value={limit}
              placeholder="без ограничения"
              onChange={(e) => setLimit(e.target.value)}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="quiz-review">Показывать разбор ответов</label>
          <select
            id="quiz-review"
            className="input"
            value={review}
            onChange={(e) => setReview(e.target.value as QuizDetail['show_review'])}
            style={{ maxWidth: 300 }}
          >
            <option value="never">Никогда</option>
            <option value="after_pass">После того, как тест сдан</option>
            <option value="always">Всегда</option>
          </select>
          <span className="muted" style={{ fontSize: '0.82rem' }}>
            «Всегда» удобно для тренировки, но раскрывает вопросы перед пересдачей.
          </span>
        </div>

        <label className="checkline">
          <input
            type="checkbox"
            checked={shuffleQ}
            onChange={(e) => setShuffleQ(e.target.checked)}
          />
          <span>Перемешивать вопросы</span>
        </label>
        <label className="checkline">
          <input
            type="checkbox"
            checked={shuffleO}
            onChange={(e) => setShuffleO(e.target.checked)}
          />
          <span>Перемешивать варианты ответа</span>
        </label>

        <SaveRow
          label="Сохранить настройки"
          disabled={busy || title.trim().length === 0}
          onSave={async () => {
            await api.authoring.updateQuiz(id, {
              title: title.trim(),
              pass_fraction: numberOr(pass, 60) / 100,
              max_attempts: numberOr(attempts, 0),
              time_limit_min: optionalNumber(limit),
              shuffle_questions: shuffleQ,
              shuffle_options: shuffleO,
              show_review: review,
            });
            load();
          }}
        />
      </section>

      <h2 style={{ margin: '28px 0 14px' }}>Вопросы теста</h2>

      {quiz.slots.length === 0 ? (
        <div className="notice">Пока в тесте нет вопросов, ученик не сможет его начать.</div>
      ) : null}

      {quiz.slots.map((slot, index) => (
        <div key={slot.id}>
          <div className="editrow">
            <span className="qcard__index">{index + 1}</span>
            <span className="editrow__main">{slot.text_md}</span>
            <span className="muted">{slot.max_mark} б.</span>
            {/*
             * Editing lives here rather than only in the bank: a teacher fixing a typo is looking
             * at the test, not at a library, and sending them to another screen to change one
             * word is how a typo survives a term.
             */}
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={busy}
              onClick={() => setEditing(editing === slot.question_id ? null : slot.question_id)}
            >
              {editing === slot.question_id ? 'Свернуть' : 'Изменить'}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--small btn--danger"
              disabled={busy}
              onClick={() => void run(() => api.authoring.removeSlot(slot.id))}
            >
              Убрать
            </button>
          </div>

          {editing === slot.question_id ? (
            <QuestionEditor questionId={slot.question_id} onSaved={load} />
          ) : null}
        </div>
      ))}

      <h2 style={{ margin: '28px 0 14px' }}>Добавить из банка</h2>

      <div className="row" style={{ marginBottom: 14 }}>
        <input
          className="input input--inline"
          placeholder="Поиск по банку"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') loadBank(search);
          }}
        />
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={() => loadBank(search)}
        >
          Найти
        </button>
        <Link to="/admin/questions" className="btn btn--ghost btn--small">
          Создать новый вопрос
        </Link>
      </div>

      {bank
        .filter((question) => !used.has(question.id))
        .map((question) => (
          <div key={question.id} className="editrow">
            <span className="editrow__main">
              <Markup text={question.text_md} />
            </span>
            <button
              type="button"
              className="btn btn--ghost btn--small"
              disabled={busy}
              onClick={() => void run(() => api.authoring.addSlot(id, question.id))}
            >
              + В тест
            </button>
          </div>
        ))}
    </>
  );
}
